// 總覽健康儀表的資料（HEALTH-01；SPEC #vms-web-827「總覽的數字與版面」，VMS UX 評審 2026-10-13 P0-8）：
// 憑證到期、韌體合規、雲錄席次、AI 額度、備份覆蓋、過去 24 小時錄影缺口（全時席次的攝影機）。
// 「沒有數字」分三種：功能沒啟用（off）／讀不到（unknown，可帶幾台沒回報）／真的是 0；載入中不顯示 0 或「—」（UX-04）。
// 韌體合規用目前範圍、跟韌體頁同一份資料（useFleetFirmware）、只算設備本體。
import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useQueries, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { vms } from '@kinio/web-sdk';
import { useScope } from '../../lib/scope';
import { backupApi, billingApi, hasOrgPerm, type BillingSubjectRef } from '../../lib/planeApi';
import type { VmsDevice } from '../../lib/devices';
import { fwState } from '../firmware/fleet';
import { useFleetFirmware } from '../firmware/FirmwarePage';
import { backupSummary, certState, firstGapStart, isContinuousRecording, localGaps, localTimelineState, seatUsage, timelineGaps, LOCAL_GAP_MIN_S, type LocalGapReason } from './health';
import { isLocalRecorder } from '../playback/view';
import { limiter } from '../../lib/limit';

/** 24 小時缺口：每台 / 每路一個 façade 時間軸請求，沒有台數上限，同時最多 6 個在飛（SPEC #vms-web-827「錄影缺口」）。 */
const gapLimit = limiter(6);

export type KpiState = { kind: 'loading' } | { kind: 'off' } | { kind: 'unknown'; missing?: number } | { kind: 'value' };

export function useHealth(tree: VmsDevice[], loading: boolean) {
  const qc = useQueryClient();
  const { orgs, homes, visible } = useScope();
  const now = Date.now();
  const all = useMemo(() => tree.flatMap((d) => [d, ...d.children]), [tree]);
  const byRef = useMemo(() => new Map(all.map((d) => [`${d.homeId}/${d.aid}`, d])), [all]);

  // 憑證到期（façade cert_not_after，設備本體）
  const certs = tree.map((d) => ({ d, s: certState(d.certNotAfter, now) })).filter((x) => x.s === 'expired' || x.s === 'soon');
  const certMissing = tree.filter((d) => !d.certNotAfter).length;
  const cert: KpiState = loading ? { kind: 'loading' } : tree.length && certMissing === tree.length ? { kind: 'unknown', missing: certMissing } : { kind: 'value' };

  // 韌體合規：跟韌體頁同一份
  const fleet = useFleetFirmware(false);
  const fwOutdated = fleet.rows.filter((r) => fwState(r.s) === 'available');
  const fwUnknown = fleet.rows.filter((r) => fwState(r.s) === 'unknown').length;
  const fw: KpiState = fleet.loading ? { kind: 'loading' } : !fleet.known ? { kind: 'unknown' } : { kind: 'value' };

  // 席次 / AI 額度：帳單主體（org 或個人家戶）
  const subjects = useMemo<BillingSubjectRef[]>(() => {
    const out: BillingSubjectRef[] = orgs
      .filter((o) => !o.notice && (hasOrgPerm(o, 'org.billing') || hasOrgPerm(o, 'org.audit')))
      .map((o) => ({ api: o.api, kind: 'org', id: o.org_id }));
    for (const h of homes)
      if (h.org_kind === 'personal' && (h.role === 'owner' || h.role === 'admin')) out.push({ api: h.endpoints.api, kind: 'home', id: h.home_id });
    return out;
  }, [orgs, homes]);
  const bills = useQueries({
    queries: subjects.map((s) => ({ queryKey: ['billing', s.kind, s.id], queryFn: () => billingApi.overview(s), staleTime: 60_000, retry: false })),
  });
  const seats = bills.flatMap((q) => q.data?.seats ?? []);
  const su = seatUsage(seats);
  const billLoading = bills.some((q) => q.isLoading);
  const billOk = bills.some((q) => q.isSuccess);
  const seat: KpiState | null = !subjects.length ? null : billLoading ? { kind: 'loading' } : !billOk ? { kind: 'unknown' } : su.total === 0 ? { kind: 'off' } : { kind: 'value' };
  const ais = bills.map((q) => q.data?.ai).filter((a) => a?.active && a.limit > 0);
  const aiUsed = ais.reduce((n, a) => n + (a?.used ?? 0), 0);
  const aiLimit = ais.reduce((n, a) => n + (a?.limit ?? 0), 0);
  const ai: KpiState | null = !subjects.length ? null : billLoading ? { kind: 'loading' } : !billOk ? { kind: 'unknown' } : !aiLimit ? { kind: 'off' } : { kind: 'value' };

  // 備份覆蓋：家戶的政策（管理者才看得到這張卡）
  const bkHomes = visible.filter((h) => !h.external && (h.role === 'owner' || h.role === 'admin' || h.can_manage));
  const bk = useQueries({
    queries: bkHomes.map((h) => ({
      queryKey: ['backup-policies', h.home_id],
      queryFn: () => backupApi.policies(h.endpoints.api, h.home_id),
      staleTime: 60_000,
      retry: false,
    })),
  });
  const bks = backupSummary(bk.flatMap((q) => q.data ?? []));
  const backup: KpiState | null = !bkHomes.length
    ? null
    : bk.some((q) => q.isLoading)
      ? { kind: 'loading' }
      : !bk.some((q) => q.isSuccess)
        ? { kind: 'unknown' }
        : bks.total === 0
          ? { kind: 'off' }
          : { kind: 'value' };

  // 過去 24 小時錄影缺口（SPEC #vms-web-827「錄影缺口」）：全時席次指派到的攝影機（雲端）＋本地錄影主機的攝影機通道（本地覆蓋）；沒有台數上限
  const contRefs = seats
    .filter((s) => s.mode === 'continuous' && s.assigned_ref && s.assigned_home_id && (s.status === 'active' || s.status === 'grace'))
    .map((s) => byRef.get(`${s.assigned_home_id}/${s.assigned_ref}`))
    .filter((d): d is VmsDevice => !!d);
  const localRefs = useMemo(() => tree.filter((d) => !d.parentAid && isLocalRecorder(d)).flatMap((d) => d.children.filter((c) => c.isCamera)), [tree]);
  const from = new Date(Math.floor(now / 600_000) * 600_000 - 86400_000).toISOString();
  const to = new Date(Math.floor(now / 600_000) * 600_000).toISOString();
  const gaps = useQueries({
    queries: contRefs.map((d) => ({
      queryKey: ['tl-24h', d.homeId, d.aid, to],
      queryFn: () =>
        gapLimit(async () => {
          const tl = await vms.timeline(d.apiEndpoint, d.homeId, d.aid, { from, to, resolution_seconds: 600 });
          const res = tl?.resolution_seconds || 600;
          return { n: timelineGaps(tl?.buckets ?? [], res), first: firstGapStart(tl?.buckets ?? [], res) };
        }),
      staleTime: 10 * 60_000,
      retry: false,
    })),
  });
  // 本地：façade 時間軸的 coverage.local / gaps.local / known_until.local / source_status.local；有缺口才再問 recorder-coverage 拿原因，
  // 同時問錄影政策看是不是全天錄影（全天錄影的沒帶原因的缺口也算，標「原因不明」；app/vms#19）。錄影政策跟錄影與儲存分頁共用快取。
  const locals = useQueries({
    queries: localRefs.map((d) => ({
      queryKey: ['tl-local-24h', d.homeId, d.aid, to],
      queryFn: () =>
        gapLimit(async () => {
          const tl = await vms.timeline(d.apiEndpoint, d.homeId, d.aid, { from, to, resolution_seconds: 600 });
          const st = localTimelineState(tl as Record<string, unknown>, Date.now());
          if (!st.reporting) return { reporting: false as const };
          if (!st.gaps.length) return { reporting: true as const, sec: 0 };
          const [cov, policy] = await Promise.all([
            vms.recorderCoverage(d.apiEndpoint, d.homeId, d.aid, { from, to }),
            qc
              .fetchQuery({ queryKey: ['rec-policy', d.homeId, d.aid], queryFn: () => vms.recordingPolicy(d.apiEndpoint, d.homeId, d.aid), staleTime: 60_000, retry: false })
              .catch(() => null),
          ]);
          const g = localGaps(cov?.data ?? [], { continuous: isContinuousRecording(policy) });
          return { reporting: true as const, sec: g.sec, first: g.first, reason: g.reason };
        }),
      staleTime: 10 * 60_000,
      retry: false,
    })),
  });
  const gapAt = new Map<string, number | undefined>();
  /** 本地缺口的攝影機 → 合計秒數最多的原因（「需要處理」那一行寫在名字後面；雲端缺口不寫） */
  const gapReason = new Map<string, LocalGapReason>();
  contRefs.forEach((d, i) => {
    if ((gaps[i]?.data?.n ?? 0) > 0) gapAt.set(d.key, gaps[i]?.data?.first);
  });
  let localChecked = 0;
  let localUnreported = 0;
  localRefs.forEach((d, i) => {
    const r = locals[i]?.data;
    if (!r || !r.reporting) {
      if (r || locals[i]?.isError) localUnreported++;
      return;
    }
    localChecked++;
    if ((r.sec ?? 0) >= LOCAL_GAP_MIN_S && !gapAt.has(d.key)) {
      gapAt.set(d.key, r.first);
      if (r.reason) gapReason.set(d.key, r.reason);
    }
  });
  const byKey = new Map([...contRefs, ...localRefs].map((d) => [d.key, d]));
  const gapCams = [...gapAt.keys()].map((k) => byKey.get(k)!).filter(Boolean);
  const cloudChecked = contRefs.length;
  const gapChecked = new Set([...contRefs.map((d) => d.key), ...localRefs.filter((_, i) => locals[i]?.data?.reporting).map((d) => d.key)]).size;
  const gap: KpiState | null =
    !subjects.length && !localRefs.length
      ? null
      : billLoading || gaps.some((q) => q.isLoading) || locals.some((q) => q.isLoading)
        ? { kind: 'loading' }
        : !cloudChecked && !localChecked
          ? localUnreported
            ? { kind: 'unknown', missing: localUnreported }
            : { kind: 'off' }
          : cloudChecked && !gaps.some((q) => q.isSuccess) && !localChecked
            ? { kind: 'unknown' }
            : { kind: 'value' };

  return {
    certs,
    cert,
    fwOutdated,
    fwUnknown,
    fwTotal: fleet.rows.length,
    fw,
    su,
    seat,
    aiUsed,
    aiLimit,
    ai,
    bks,
    backup,
    contRefs,
    gapCams,
    /** 有缺口的攝影機 → 第一個缺口開始（秒；回放落點） */
    gapAt,
    gapReason,
    gapChecked,
    cloudChecked,
    localChecked,
    localUnreported,
    gap,
  };
}

/** 一張健康卡：整張是連結；沒有數字時照三種寫法（未啟用〔設定〕／讀不到（N 台沒回報）／0）。 */
export function Kpi({
  id,
  label,
  to,
  state,
  value,
  note,
  tip,
  tone,
  setupTo,
  setupLabel,
  offNote,
  small,
}: {
  id: string;
  label: string;
  to: string;
  state: KpiState;
  value?: React.ReactNode;
  note?: React.ReactNode;
  tip?: string;
  tone?: 'err' | 'warn';
  setupTo?: string;
  /** 未啟用時按鈕的字（預設「設定」） */
  setupLabel?: string;
  /** 未啟用時另外說一句（例：「另有 1 台由家戶方案提供雲錄」） */
  offNote?: React.ReactNode;
  /** 值是一句話（例「1 台設備、3 個通道」）時用小一號的字 */
  small?: boolean;
}) {
  const { t } = useTranslation();
  const color = tone === 'err' ? 'var(--err)' : tone === 'warn' ? 'var(--warn)' : undefined;
  if (state.kind === 'off')
    return (
      <div className="card kpi" data-kpi={id} data-state="off" title={tip} style={{ order: 1 }}>
        <div className="muted">{label}</div>
        <div className="value value-text">{t('health.not_enabled')}</div>
        {offNote && <div className="muted small">{offNote}</div>}
        {setupTo && (
          <Link className="btn btn-sm" to={setupTo}>
            {setupLabel ?? t('health.setup')}
          </Link>
        )}
      </div>
    );
  return (
    // 有異常的卡排前面（CSS order，不改 DOM 順序；第三輪 R3-P2-2：11 張卡沒有主次）、未啟用的排最後
    <Link
      className={'card kpi kpi-link' + (tone && state.kind === 'value' ? ' kpi-' + tone : '')}
      to={to}
      data-kpi={id}
      data-state={state.kind}
      data-tone={state.kind === 'value' ? (tone ?? '') : ''}
      title={tip}
      style={{ order: state.kind === 'value' && tone === 'err' ? -2 : state.kind === 'value' && tone === 'warn' ? -1 : 0 }}
    >
      <div className="muted">{label}</div>
      {state.kind === 'loading' ? (
        <div className="skeleton-bar kpi-skel" aria-label={t('common.loading')} />
      ) : state.kind === 'unknown' ? (
        <div className="value value-text">{state.missing ? t('health.unknown_n', { count: state.missing }) : t('health.unknown')}</div>
      ) : (
        <div className={'value' + (small ? ' value-sm' : '')} style={{ color }}>
          {value}
        </div>
      )}
      {note && state.kind !== 'loading' && <div className="muted small">{note}</div>}
    </Link>
  );
}
